import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { concat, EMPTY, of } from 'rxjs';
import { map, exhaustMap, catchError, withLatestFrom, switchMap } from 'rxjs/operators';
import { UserDetailsService } from '../../services/user-details/user-details.service';
import * as UserDetailsActions from './user-details.actions';
import * as sharedAction from '../shared/shared.actions';
import * as DashboardActions from '../dashboard/dashboard.actions';
import * as AuthActions from '../auth/auth.actions';
import * as UserDetailsSelector from './user-details.selectors';
import { select, Store } from '@ngrx/store';
import { DashboardFacade } from '../dashboard/dashboard.facade';
import { NgxSpinnerService } from 'ngx-spinner';
import { SharedService } from '../../services/shared/shared.service';
import { CookieService } from 'ngx-cookie-service';
import * as jwt_decode from 'jwt-decode';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import {
  ExpireSoonDescriptionStr,
  ExpireSoonStr,
  SessionExpiredMessage,
} from '@piqet-angular/shared';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { MenuService } from 'libs/features/src/lib/menu/menu.service';
import { CookieHelperService } from 'libs/shared/src/lib/service/cookie-helper.service';
import { SimultaneousService } from '../../services/shared/simultaneous.service';
import { CommonService } from 'libs/shared/src/lib/service/common.service';
import { SessionService } from 'libs/shared/src/lib/service/session.service';
@Injectable()
export class UserDetailsEffects {
  private actions$ = inject(Actions);
  userPersonalInfo: any;

  // Effects for userProfileOverview
  userProfileOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.userProfileOverview),
      withLatestFrom(this.store.pipe(select(UserDetailsSelector.getuserProfileOverview))),
      exhaustMap(action => {
        return this.UserDetailsService.userProfileOverview().pipe(
          map((data: any) => {
            return UserDetailsActions.userProfileOverviewSuccess({
              userProfileOverview: data.data,
            });
          }),
          catchError((error: any) => of(UserDetailsActions.userProfileOverviewFailure(error)))
        );
      })
    )
  );

  userProfileOverviewSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.userProfileOverviewSuccess),
      exhaustMap(action => {
        return of(UserDetailsActions.getUserProfileImage());
      })
    )
  );

  //Effects for getUserPersonalInfo.
  getUserPersonalInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.getUserPersonalInfo),
      exhaustMap(action => {
        return this.UserDetailsService.userPersonalInfo().pipe(
          map((data: any) => {
            return UserDetailsActions.getUserPersonalInfoSuccess({
              userPersonalInfo: data.data,
            });
          }),
          catchError((error: any) => of(UserDetailsActions.getUserPersonalInfoFailure(error)))
        );
      })
    )
  );

  // Effects for saveUserPersonalData.
  saveUserPersonalData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.saveUserPersonalInfo),
      exhaustMap(action => {
        this.userPersonalInfo = action.payload;
        return this.UserDetailsService.saveUserPersonalInfo(action.payload).pipe(
          map((data: any) => {
            return UserDetailsActions.saveUserPersonalInfoSuccess({
              userProfileOverview: action.payload,
            });
          }),
          catchError((error: any) => of(UserDetailsActions.saveUserPersonalInfo(error)))
        );
      })
    )
  );

  saveUserPersonalInfoSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.saveUserPersonalInfoSuccess),
      exhaustMap(action => {
        return of(
          UserDetailsActions.getUserPersonalInfo(),
          sharedAction.ToasterSuccess({
            description: 'Profile updated successfully.',
          })
        );
      })
    )
  );

  // // Effects for loggedInUserProfile
  loggedInUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.loggedInUserProfile),
      withLatestFrom(this.store.pipe(select(UserDetailsSelector.getProfileData))),
      exhaustMap(action => {
        if (!action[action.length - 1]) {
          return this.UserDetailsService.loggedInUserProfileSummary().pipe(
            map((data: any) => {
              this.cookieService.set('organizationName', data.data?.organizationName);
              return UserDetailsActions.loggedInUserProfileSuccess({
                profileData: data.data,
              });
            }),
            catchError((error: any) => of(UserDetailsActions.loggedInUserProfileFailure(error)))
          );
        } else {
          return of();
        }
      })
    )
  );

  loggedInUserProfileSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.loggedInUserProfileSuccess),
      exhaustMap(action => {
        return of(
          //UserDetailsActions.getUserProfileImage(),
          UserDetailsActions.getOrganization()
        );
      })
    )
  );

  // Effects for getUserProfileImage
  getUserProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.getUserProfileImage),
      withLatestFrom(this.store.pipe(select(UserDetailsSelector.getUserProfileImageData))),
      exhaustMap(action => {
        if (!action[action.length - 1]) {
          return this.UserDetailsService.getUserProfileImage().pipe(
            map((data: any) => {
              return UserDetailsActions.getUserProfileImageSuccess({
                userProfileImageData: data.data,
              });
            }),
            catchError((error: any) => of(UserDetailsActions.getUserProfileImageFailure(error)))
          );
        } else {
          return of();
        }
      })
    )
  );

  getUserProfileImageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.getUserProfileImageSuccess),
      exhaustMap(() => {
        return of(UserDetailsActions.getUserPersonalInfo());
      })
    )
  );

  // Effects for userProfileImage
  updateUserProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.updateUserProfileImage),
      exhaustMap(action => {
        return this.UserDetailsService.updateUserProfileImage(action.payload).pipe(
          map((data: any) => {
            return UserDetailsActions.updateUserProfileImageSuccess({
              userProfileImageData: action.payload.imageStr,
              userImageStatus: data.data,
            });
          }),
          catchError((error: any) => of(UserDetailsActions.updateUserProfileImageFailure(error)))
        );
      })
    )
  );

  updateUserProfileImageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.updateUserProfileImageSuccess),
      exhaustMap(() => {
        return of(
          UserDetailsActions.getUserProfileImage(),
          UserDetailsActions.loggedInUserProfile()
        );
      })
    )
  );

  // // Effects for getUserEmailPreferences
  getUserEmailPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.getUserEmailPreferences),
      exhaustMap(action => {
        return this.UserDetailsService.getUserEmailPreferences().pipe(
          map((data: any) => {
            return UserDetailsActions.getUserEmailPreferencesSuccess({
              userEmailPreferencesStatus: data.data,
            });
          }),
          catchError((error: any) => of(UserDetailsActions.getUserEmailPreferencesFailure(error)))
        );
      })
    )
  );

  // Effects for updateUserEmailPreferences
  updateUserEmailPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.updateUserEmailPreferences),
      exhaustMap(action => {
        return this.UserDetailsService.updateUserEmailPreferences(action.payload).pipe(
          map((data: any) => {
            return UserDetailsActions.updateUserEmailPreferencesSuccess({
              userEmailPreferencesStatus: data.data,
            });
          }),
          catchError((error: any) =>
            of(UserDetailsActions.updateUserEmailPreferencesFailure(error))
          )
        );
      })
    )
  );

  updateUserEmailPreferencesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.updateUserEmailPreferencesSuccess),
      exhaustMap(() => {
        return of(
          sharedAction.ToasterSuccess({
            description: 'Email preference saved successfully!',
          })
        );
      })
    )
  );

  // Effects for getOrganization
  getOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.getOrganization),
      exhaustMap(action => {
        return this.UserDetailsService.getOrganization().pipe(
          map((data: any) => {
            let orgName = data.data.find((x: any) => x.isCheck);
            return UserDetailsActions.getOrganizationSuccess({
              organizationList: data.data,
              selectedOrg: orgName?.name,
            });
          }),
          catchError((error: any) => of(UserDetailsActions.getOrganizationFailure(error)))
        );
      })
    )
  );

  // Effects for changeOrganization
  changeOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.changeOrganization),
      exhaustMap(action => {
        return this.UserDetailsService.changeOrganization(action.payload).pipe(
          map((data: any) => {
            return UserDetailsActions.changeOrganizationSuccess({
              organizationData: data,
            });
          }),
          catchError((error: any) => of(UserDetailsActions.changeOrganizationFailure({ error })))
        );
      })
    )
  );

  changeOrganizationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.changeOrganizationSuccess),
      exhaustMap(action => {
        this.cookieService.set('organizationId', action.organizationData.data);
        return of(UserDetailsActions.regenerateAccessToken({ sessionString: '' }));
      })
    )
  );

  regenerateAccessToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.regenerateAccessToken),
      exhaustMap(action => {
        return this.UserDetailsService.regenerateAccessToken(
          this.cookieService.get('userId'),
          this.commonService.initDeviceId()
        ).pipe(
          switchMap(async (data: any) => {
            const tokenExpiry: any = jwt_decode.jwtDecode(data.data.token);
            const expiry = new Date(tokenExpiry.exp * 1000);

            // Update token atomically - set new token before deleting old one
            this.cookieHelper.updateTokenOnly(data.data.token, expiry);
            this.cookieHelper.updateExpiry(expiry);

            this.simultaneousService.notifyTokenRefreshed(data.data.token);
            this.dashBoardFacade.clearDashboradData();
            if (!action.sessionString) {
              this.router.navigate([`/dashboard`]);
            }
            setTimeout(async () => {
              await this.sessionService.getAccessModuleData();
            }, 100);
            return UserDetailsActions.regenerateAccessTokenSuccess({
              tokenClaim: data.data.token,
              organizationName: data.data.organizationName,
              showToast: action.sessionString ? true : false,
            });
          }),
          catchError((error: any) => of(UserDetailsActions.regenerateAccessTokenFailure({ error })))
        );
      })
    )
  );

  regenerateAccessTokenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActions.regenerateAccessTokenSuccess),
      exhaustMap(action => {
        const baseActions = [
          UserDetailsActions.loggedInUserProfile(),
          DashboardActions.getDashboardData(),
        ];

        if (!action.showToast) {
          return of(
            AuthActions.loginSuccess({
              tokenClaim: action.tokenClaim,
              isTokenUpdate: true,
            }),
            ...baseActions
          );
        }

        return of(...baseActions);
      })
    )
  );
  deviceId: any;

  constructor(
    private router: Router,
    private UserDetailsService: UserDetailsService,
    private store: Store,
    private dashBoardFacade: DashboardFacade,
    private authService: AuthenticationService,
    private cookieService: CookieService,
    private swalService: SwalService,
    private menuService: MenuService,
    private cookieHelper: CookieHelperService,
    private simultaneousService: SimultaneousService,
    private commonService: CommonService,
    private sessionService: SessionService
  ) {
    this.deviceId = this.commonService.initDeviceId();
  }
}
